/**
 * Client-side unit inference from column names and sample values.
 * Used in the import wizard to pre-populate the units confirmation step.
 *
 * Detection priority:
 *  1. Categorical column names → no unit (never assign)
 *  2. "Contains unit values" columns (e.g. activity_units) → flagged, no unit assigned
 *  3. Sample-value check: if all samples are non-numeric strings → categorical → no unit
 *  4. Explicit unit suffix in column name (_nM, (µM), etc.) → high confidence
 *  5. Known assay / measurement name → default unit from mapping
 *  6. Fallback → "not specified"
 */

export interface InferredUnit {
  columnName: string;
  unit: string | null;
  unitType: string;
  confidence: 'high' | 'medium' | 'low' | 'none';
  displayName: string;
  /** True if this column CONTAINS unit strings rather than needing a unit assigned */
  containsUnitValues?: boolean;
  /** True if this column is categorical text — should never have a unit */
  isCategorical?: boolean;
}

// ─── Grouped unit options for the dropdown selector ─────────────────────────

export const UNIT_OPTIONS: { group: string; unitType: string; units: { value: string | null; label: string }[] }[] = [
  {
    group: 'Concentration (Molar)',
    unitType: 'concentration_molar',
    units: [
      { value: 'pM', label: 'pM' },
      { value: 'nM', label: 'nM' },
      { value: 'µM', label: 'µM' },
      { value: 'mM', label: 'mM' },
      { value: 'M', label: 'M' },
    ],
  },
  {
    group: 'Concentration (Mass/Vol)',
    unitType: 'concentration_mass',
    units: [
      { value: 'pg/mL', label: 'pg/mL' },
      { value: 'ng/mL', label: 'ng/mL' },
      { value: 'µg/mL', label: 'µg/mL' },
      { value: 'mg/mL', label: 'mg/mL' },
      { value: 'g/L', label: 'g/L' },
    ],
  },
  {
    group: 'Weight',
    unitType: 'weight',
    units: [
      { value: 'Da', label: 'Da' },
      { value: 'kDa', label: 'kDa' },
      { value: 'MDa', label: 'MDa' },
      { value: 'g/mol', label: 'g/mol' },
    ],
  },
  {
    group: 'Time',
    unitType: 'time',
    units: [
      { value: 's', label: 's' },
      { value: 'min', label: 'min' },
      { value: 'h', label: 'h' },
      { value: 'd', label: 'days' },
    ],
  },
  {
    group: 'Percentage',
    unitType: 'percentage',
    units: [
      { value: '%', label: '%' },
    ],
  },
  {
    group: 'Area',
    unitType: 'area',
    units: [
      { value: 'Å²', label: 'Å²' },
    ],
  },
  {
    group: 'Rate / Kinetics',
    unitType: 'rate',
    units: [
      { value: 'nmol/min/mg', label: 'nmol/min/mg' },
      { value: 's⁻¹', label: 's⁻¹' },
      { value: 'M⁻¹s⁻¹', label: 'M⁻¹s⁻¹' },
    ],
  },
  {
    group: 'Clearance',
    unitType: 'clearance',
    units: [
      { value: 'µL/min/mg', label: 'µL/min/mg' },
      { value: 'mL/min/kg', label: 'mL/min/kg' },
    ],
  },
  {
    group: 'Volume',
    unitType: 'volume',
    units: [
      { value: 'L/kg', label: 'L/kg' },
    ],
  },
  {
    group: 'Permeability',
    unitType: 'permeability',
    units: [
      { value: '10⁻⁶ cm/s', label: '10⁻⁶ cm/s' },
    ],
  },
  {
    group: 'Dose',
    unitType: 'dose',
    units: [
      { value: 'mg/kg', label: 'mg/kg' },
    ],
  },
  {
    group: 'Exposure',
    unitType: 'exposure',
    units: [
      { value: 'ng·h/mL', label: 'ng·h/mL' },
      { value: 'µM·h', label: 'µM·h' },
    ],
  },
];

// ─── Categorical column name patterns (NEVER assign a unit) ─────────────────

const CATEGORICAL_COLUMN_PATTERNS: RegExp[] = [
  // Activity type / assay type columns (values: IC50, EC50, Ki, etc.)
  /^activity[\s._-]?type$|^standard[\s._-]?type$|^assay[\s._-]?type$|^measurement[\s._-]?type$|^endpoint[\s._-]?type$|^bioactivity[\s._-]?type$|^result[\s._-]?type$/i,
  /^type$/i,
  /^endpoint$/i,

  // Activity relation / qualifier columns (values: =, >, <, >=, etc.)
  /^activity[\s._-]?relation$|^standard[\s._-]?relation$|^relation$|^qualifier$|^operator$|^comparator$/i,

  // Description / text columns
  /^assay[\s._-]?description$|^description$|^assay[\s._-]?name$|^assay$|^protocol$|^method$|^comments?$/i,

  // Target name columns
  /^target[\s._-]?name$|^target$|^gene[\s._-]?name$|^gene[\s._-]?symbol$|^protein[\s._-]?name$|^target[\s._-]?id$|^uniprot[\s._-]?id$|^gene[\s._-]?id$|^assay[\s._-]?target$/i,

  // Compound name / identifier columns
  /^molecule[\s._-]?name$|^compound[\s._-]?name$|^compound[\s._-]?id$|^molecule[\s._-]?id$|^name$/i,

  // Structure columns
  /^molecule[\s._-]?smiles$|^smiles$|^SMILES$|^canonical[\s._-]?smiles$|^compound[\s._-]?smiles$|^inchi$|^inchi[\s._-]?key$/i,

  // Identifier columns
  /^batch[\s._-]?id$|^lot[\s._-]?id$|^lot[\s._-]?number$|^batch[\s._-]?number$|^sample[\s._-]?id$|^plate[\s._-]?id$|^well$/i,

  // Reference columns
  /^reference$|^citation$|^source$|^doi$|^pubmed[\s._-]?id$|^pmid$|^journal$/i,

  // Ames test (categorical positive/negative)
  /^ames$/i,
];

// ─── "Contains unit values" column patterns ─────────────────────────────────

const UNIT_VALUE_COLUMN_PATTERNS: RegExp[] = [
  /^activity[\s._-]?units?$|^standard[\s._-]?units?$|^units?$|^measurement[\s._-]?units?$/i,
];

// ─── Unit inference patterns (ordered: explicit suffixes first, then names) ──

type Confidence = 'high' | 'medium';

const UNIT_PATTERNS: { pattern: RegExp; unit: string | null; unitType: string; confidence: Confidence }[] = [
  // ── Explicit unit suffixes (HIGH confidence — unit is literally in the name) ──

  // Concentration - molar
  { pattern: /_nM$|[\s(]nM[\s)]|\(nM\)/i, unit: 'nM', unitType: 'concentration_molar', confidence: 'high' },
  { pattern: /_[uµ]M$|[\s(][uµ]M[\s)]|\([uµ]M\)/i, unit: 'µM', unitType: 'concentration_molar', confidence: 'high' },
  { pattern: /_mM$|[\s(]mM[\s)]|\(mM\)/i, unit: 'mM', unitType: 'concentration_molar', confidence: 'high' },
  { pattern: /_pM$|[\s(]pM[\s)]|\(pM\)/i, unit: 'pM', unitType: 'concentration_molar', confidence: 'high' },
  { pattern: /\(M\)$/, unit: 'M', unitType: 'concentration_molar', confidence: 'high' },

  // Concentration - mass/volume
  { pattern: /ng\/mL|ng\/ml/i, unit: 'ng/mL', unitType: 'concentration_mass', confidence: 'high' },
  { pattern: /[uµ]g\/mL|[uµ]g\/ml/i, unit: 'µg/mL', unitType: 'concentration_mass', confidence: 'high' },
  { pattern: /mg\/mL|mg\/ml/i, unit: 'mg/mL', unitType: 'concentration_mass', confidence: 'high' },

  // Weight — explicit
  { pattern: /_Da$|[\s(]Da[\s)]|\(Da\)/i, unit: 'Da', unitType: 'weight', confidence: 'high' },
  { pattern: /_kDa$|[\s(]kDa[\s)]|\(kDa\)/i, unit: 'kDa', unitType: 'weight', confidence: 'high' },

  // Clearance — explicit
  { pattern: /[uµ]L\/min\/mg/i, unit: 'µL/min/mg', unitType: 'clearance', confidence: 'high' },
  { pattern: /mL\/min\/kg/i, unit: 'mL/min/kg', unitType: 'clearance', confidence: 'high' },

  // Dose — explicit
  { pattern: /mg\/kg/i, unit: 'mg/kg', unitType: 'dose', confidence: 'high' },

  // Permeability — explicit
  { pattern: /10[\s._-]?[⁻-]6[\s._-]?cm\/s|cm\/s/i, unit: '10⁻⁶ cm/s', unitType: 'permeability', confidence: 'high' },

  // Volume — explicit
  { pattern: /L\/kg/i, unit: 'L/kg', unitType: 'volume', confidence: 'high' },

  // Rate — explicit
  { pattern: /M[\s._-]?[⁻-]1[\s._-]?s[\s._-]?[⁻-]1/i, unit: 'M⁻¹s⁻¹', unitType: 'rate', confidence: 'high' },
  { pattern: /s[\s._-]?[⁻-]1/i, unit: 's⁻¹', unitType: 'rate', confidence: 'high' },

  // Percentage — explicit
  { pattern: /_%$|\(%\)$|^percent/i, unit: '%', unitType: 'percentage', confidence: 'high' },

  // Time — explicit
  { pattern: /_hr$|_hours?$|[\s(]h[\s)]/i, unit: 'h', unitType: 'time', confidence: 'high' },
  { pattern: /_min$|_minutes?$/i, unit: 'min', unitType: 'time', confidence: 'high' },
  { pattern: /_sec$|_seconds?$/i, unit: 's', unitType: 'time', confidence: 'high' },

  // Temperature — explicit
  { pattern: /_°?C$|celsius/i, unit: '°C', unitType: 'temperature', confidence: 'high' },

  // ── Dimensionless — known property names ──────────────────────────────────

  { pattern: /^[Cc]?[Ll]og[PD]$|^ALogP$|^XLogP$|^[Ss]LogP$|^cx_log[pd]$/i, unit: null, unitType: 'dimensionless', confidence: 'high' },
  { pattern: /^LogD$/i, unit: null, unitType: 'dimensionless', confidence: 'high' },
  { pattern: /^pKa$/i, unit: null, unitType: 'dimensionless', confidence: 'high' },
  { pattern: /^HBA$|^HBD$|^NumH/i, unit: null, unitType: 'dimensionless', confidence: 'high' },
  { pattern: /^Num.*Count$|^Ring.*Count$|^Rotatable|^RotBonds$|^rotatable_bonds$|^aromatic_rings$|^heavy_atoms$/i, unit: null, unitType: 'dimensionless', confidence: 'high' },
  { pattern: /^Lipinski$|^Veber$|^PAINS$|^Fsp3$|^lipinski_violations$/i, unit: null, unitType: 'dimensionless', confidence: 'high' },
  { pattern: /^QED$/i, unit: null, unitType: 'dimensionless', confidence: 'high' },
  { pattern: /^Fraction|^Ratio/i, unit: null, unitType: 'dimensionless', confidence: 'medium' },

  // p-scale values (dimensionless: -log10 of concentration)
  { pattern: /^p(IC50|EC50|AC50|IC90|IC99|GI50|CC50|LC50|Ki|Kd|Kb)$/i, unit: null, unitType: 'dimensionless', confidence: 'high' },

  // Z-score / Z-prime / signal ratios
  { pattern: /^z[\s._-]?score$/i, unit: null, unitType: 'dimensionless', confidence: 'high' },
  { pattern: /^z[\s._-]?prime$|^z'$/i, unit: null, unitType: 'dimensionless', confidence: 'high' },
  { pattern: /^s\/b$|^signal[\s._-]?to[\s._-]?background$/i, unit: null, unitType: 'dimensionless', confidence: 'high' },
  { pattern: /^s\/n$|^signal[\s._-]?to[\s._-]?noise$/i, unit: null, unitType: 'dimensionless', confidence: 'high' },
  { pattern: /^efflux[\s._-]?ratio$/i, unit: null, unitType: 'dimensionless', confidence: 'high' },
  { pattern: /^therapeutic[\s._-]?index$/i, unit: null, unitType: 'dimensionless', confidence: 'high' },
  { pattern: /^selectivity[\s._-]?index$|^SI$/i, unit: null, unitType: 'dimensionless', confidence: 'medium' },

  // ── Percentage — inferred from common names ───────────────────────────────

  { pattern: /^%[\s._-]?inhibition$|^percent[\s._-]?inhibition$|^%_?inhibition$/i, unit: '%', unitType: 'percentage', confidence: 'high' },
  { pattern: /^%[\s._-]?activity$|^percent[\s._-]?activity$|^%_?activity$/i, unit: '%', unitType: 'percentage', confidence: 'high' },
  { pattern: /^%[\s._-]?response$|^percent[\s._-]?response$/i, unit: '%', unitType: 'percentage', confidence: 'high' },
  { pattern: /^%[\s._-]?viability$|^percent[\s._-]?viability$|^%_?viability$/i, unit: '%', unitType: 'percentage', confidence: 'high' },
  { pattern: /^%[\s._-]?cytotoxicity$|^percent[\s._-]?cytotoxicity$/i, unit: '%', unitType: 'percentage', confidence: 'high' },
  { pattern: /inhibition$/i, unit: '%', unitType: 'percentage', confidence: 'medium' },
  { pattern: /^F$|^%F$|^oral[\s._-]?bioavailability$/i, unit: '%', unitType: 'percentage', confidence: 'medium' },
  { pattern: /^fu$|^fraction[\s._-]?unbound$|^PPB$|^plasma[\s._-]?protein[\s._-]?binding$/i, unit: '%', unitType: 'percentage', confidence: 'medium' },

  // ── Weight — inferred from common names ───────────────────────────────────

  { pattern: /^MW$|^MolWeight$|^mol_wt$|^molecular[\s._-]?weight$/i, unit: 'Da', unitType: 'weight', confidence: 'medium' },
  { pattern: /^ExactMass$|^exact[\s._-]?mass$|^mol_wt_monoisotopic$/i, unit: 'Da', unitType: 'weight', confidence: 'medium' },

  // ── Area — inferred ───────────────────────────────────────────────────────

  { pattern: /^tPSA$|^TPSA$|^PSA$|polar[\s._-]?surface[\s._-]?area/i, unit: 'Å²', unitType: 'area', confidence: 'medium' },

  // ── Time — inferred ───────────────────────────────────────────────────────

  { pattern: /^t1\/?2$|^half[\s._-]?life$/i, unit: 'h', unitType: 'time', confidence: 'medium' },
  { pattern: /^Tmax$/i, unit: 'h', unitType: 'time', confidence: 'medium' },

  // ── Potency / binding (concentration, nM default) ─────────────────────────

  { pattern: /^IC50$/i, unit: 'nM', unitType: 'concentration_molar', confidence: 'medium' },
  { pattern: /^EC50$/i, unit: 'nM', unitType: 'concentration_molar', confidence: 'medium' },
  { pattern: /^AC50$/i, unit: 'nM', unitType: 'concentration_molar', confidence: 'medium' },
  { pattern: /^Ki$/i, unit: 'nM', unitType: 'concentration_molar', confidence: 'medium' },
  { pattern: /^Kd$/i, unit: 'nM', unitType: 'concentration_molar', confidence: 'medium' },
  { pattern: /^Kb$/i, unit: 'nM', unitType: 'concentration_molar', confidence: 'medium' },
  { pattern: /^KD$/i, unit: 'nM', unitType: 'concentration_molar', confidence: 'medium' },  // SPR binding
  { pattern: /^IC90$|^IC99$/i, unit: 'nM', unitType: 'concentration_molar', confidence: 'medium' },
  { pattern: /^GI50$/i, unit: 'nM', unitType: 'concentration_molar', confidence: 'medium' },
  { pattern: /^CC50$/i, unit: 'nM', unitType: 'concentration_molar', confidence: 'medium' },
  { pattern: /^LC50$/i, unit: 'nM', unitType: 'concentration_molar', confidence: 'medium' },
  { pattern: /^Potency$/i, unit: 'nM', unitType: 'concentration_molar', confidence: 'medium' },

  // ── Safety IC50s (typically reported in µM) ───────────────────────────────

  { pattern: /^hERG[\s._-]?IC50$/i, unit: 'µM', unitType: 'concentration_molar', confidence: 'medium' },
  { pattern: /^CYP[\s._-]?(3A4|2D6|2C9|2C19|1A2)[\s._-]?IC50$/i, unit: 'µM', unitType: 'concentration_molar', confidence: 'medium' },
  { pattern: /^cytotoxicity[\s._-]?CC50$/i, unit: 'µM', unitType: 'concentration_molar', confidence: 'medium' },

  // ── Microbiology (µg/mL default) ──────────────────────────────────────────

  { pattern: /^MIC$|^MIC90$/i, unit: 'µg/mL', unitType: 'concentration_mass', confidence: 'medium' },
  { pattern: /^MBC$/i, unit: 'µg/mL', unitType: 'concentration_mass', confidence: 'medium' },
  { pattern: /^MFC$/i, unit: 'µg/mL', unitType: 'concentration_mass', confidence: 'medium' },

  // ── Kinetic constants ─────────────────────────────────────────────────────

  { pattern: /^Km$/i, unit: 'µM', unitType: 'concentration_molar', confidence: 'medium' },
  { pattern: /^Vmax$/i, unit: 'nmol/min/mg', unitType: 'rate', confidence: 'medium' },
  { pattern: /^kcat$/i, unit: 's⁻¹', unitType: 'rate', confidence: 'medium' },
  { pattern: /^kcat[\s._/-]?Km$/i, unit: 'M⁻¹s⁻¹', unitType: 'rate', confidence: 'medium' },
  { pattern: /^kon$|^ka$/i, unit: 'M⁻¹s⁻¹', unitType: 'rate', confidence: 'medium' },
  { pattern: /^koff$/i, unit: 's⁻¹', unitType: 'rate', confidence: 'medium' },

  // ── ADMET / DMPK ─────────────────────────────────────────────────────────

  { pattern: /^CLint$|^intrinsic[\s._-]?clearance$/i, unit: 'µL/min/mg', unitType: 'clearance', confidence: 'medium' },
  { pattern: /^CL$|^clearance$/i, unit: 'mL/min/kg', unitType: 'clearance', confidence: 'medium' },
  { pattern: /^Vd$|^Vss$|^volume[\s._-]?of[\s._-]?distribution$/i, unit: 'L/kg', unitType: 'volume', confidence: 'medium' },
  { pattern: /^AUC$/i, unit: 'ng·h/mL', unitType: 'exposure', confidence: 'medium' },
  { pattern: /^Cmax$/i, unit: 'ng/mL', unitType: 'concentration_mass', confidence: 'medium' },
  { pattern: /^Solubility$/i, unit: 'µg/mL', unitType: 'concentration_mass', confidence: 'medium' },
  { pattern: /^Papp$|^permeability$/i, unit: '10⁻⁶ cm/s', unitType: 'permeability', confidence: 'medium' },
  { pattern: /^Caco[\s._-]?2([\s._-]?Papp)?$/i, unit: '10⁻⁶ cm/s', unitType: 'permeability', confidence: 'medium' },
  { pattern: /^PAMPA$/i, unit: '10⁻⁶ cm/s', unitType: 'permeability', confidence: 'medium' },
  { pattern: /^MDCK([\s._-]?Papp)?$/i, unit: '10⁻⁶ cm/s', unitType: 'permeability', confidence: 'medium' },

  // ── In vivo dose-based endpoints ──────────────────────────────────────────

  { pattern: /^ED50$/i, unit: 'mg/kg', unitType: 'dose', confidence: 'medium' },
  { pattern: /^LD50$/i, unit: 'mg/kg', unitType: 'dose', confidence: 'medium' },
  { pattern: /^TD50$/i, unit: 'mg/kg', unitType: 'dose', confidence: 'medium' },
  { pattern: /^NOAEL$/i, unit: 'mg/kg', unitType: 'dose', confidence: 'medium' },
  { pattern: /^MTD$/i, unit: 'mg/kg', unitType: 'dose', confidence: 'medium' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Check if a string looks numeric (int, float, scientific notation) */
function isNumericString(s: string): boolean {
  if (!s || s.trim() === '') return false;
  const trimmed = s.trim();
  // Handles: 123, 1.23, -1.23, 1.23e-4, >10, <0.5, >=100, ~5.0
  const cleaned = trimmed.replace(/^[><=~≥≤]+\s*/, '');
  return /^-?\d+\.?\d*([eE][+-]?\d+)?$/.test(cleaned);
}

// ─── Cross-column unit pairing ──────────────────────────────────────────────
// Maps value column name patterns to their paired unit-value column name patterns.
// If an activity_units column exists, its sample values inform the unit of activity_value.

const VALUE_UNIT_PAIRS: { valueCol: RegExp; unitCol: RegExp }[] = [
  { valueCol: /^activity[\s._-]?value$/i, unitCol: /^activity[\s._-]?units?$/i },
  { valueCol: /^standard[\s._-]?value$/i, unitCol: /^standard[\s._-]?units?$/i },
  { valueCol: /^value$/i, unitCol: /^units?$/i },
  { valueCol: /^result$/i, unitCol: /^units?$|^result[\s._-]?units?$/i },
];

/**
 * Given sample values from a "units" column (e.g. activity_units), find the most common
 * unit string and return it paired with a unitType lookup.
 */
function inferUnitFromPairedColumn(samples: any[]): { unit: string; unitType: string } | null {
  const counts: Record<string, number> = {};
  for (const v of samples) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s === '') continue;
    counts[s] = (counts[s] || 0) + 1;
  }
  const entries = Object.entries(counts);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  const unit = entries[0][0];
  return { unit, unitType: unitTypeForUnit(unit) || 'unspecified' };
}

/**
 * Infer units for a list of column names.
 * Optionally accepts sample values per column for value-based categorical detection.
 *
 * @param columnNames - list of column header names
 * @param columnSamples - optional map of column name -> sample values for value-based detection
 */
export function inferUnits(
  columnNames: string[],
  columnSamples?: Record<string, any[]>,
): InferredUnit[] {
  // Pre-compute: find paired unit columns so value columns can inherit their unit
  const pairedUnitFor: Record<string, { unit: string; unitType: string }> = {};
  if (columnSamples) {
    for (const pair of VALUE_UNIT_PAIRS) {
      const valueCols = columnNames.filter(n => pair.valueCol.test(n));
      const unitCol = columnNames.find(n => pair.unitCol.test(n));
      if (valueCols.length > 0 && unitCol && columnSamples[unitCol]) {
        const inferred = inferUnitFromPairedColumn(columnSamples[unitCol]);
        if (inferred) {
          for (const vc of valueCols) {
            pairedUnitFor[vc] = inferred;
          }
        }
      }
    }
  }

  return columnNames.map(name => {
    // ── Step 1: Check categorical column name exclusions ──
    for (const pattern of CATEGORICAL_COLUMN_PATTERNS) {
      if (pattern.test(name)) {
        return {
          columnName: name,
          unit: null,
          unitType: 'categorical',
          confidence: 'high' as const,
          displayName: name,
          isCategorical: true,
        };
      }
    }

    // ── Step 2: Check if column CONTAINS unit values ──
    for (const pattern of UNIT_VALUE_COLUMN_PATTERNS) {
      if (pattern.test(name)) {
        return {
          columnName: name,
          unit: null,
          unitType: 'unit_values',
          confidence: 'high' as const,
          displayName: `${name} (contains unit values)`,
          containsUnitValues: true,
        };
      }
    }

    // ── Step 3: Value-based categorical detection ──
    if (columnSamples && columnSamples[name]) {
      const samples = columnSamples[name].filter(v => v != null && String(v).trim() !== '');
      if (samples.length > 0) {
        const numericCount = samples.filter(v => isNumericString(String(v))).length;
        // If none of the sample values are numeric, it's categorical
        if (numericCount === 0) {
          return {
            columnName: name,
            unit: null,
            unitType: 'categorical',
            confidence: 'medium' as const,
            displayName: name,
            isCategorical: true,
          };
        }
      }
    }

    // ── Step 3b: Cross-column unit inference (paired unit column) ──
    if (pairedUnitFor[name]) {
      const paired = pairedUnitFor[name];
      return {
        columnName: name,
        unit: paired.unit,
        unitType: paired.unitType,
        confidence: 'medium' as const,
        displayName: `${name} (${paired.unit} — from paired column)`,
      };
    }

    // ── Step 4: Pattern-based unit inference ──
    for (const { pattern, unit, unitType, confidence } of UNIT_PATTERNS) {
      if (pattern.test(name)) {
        const displayName = unit ? `${name} (${unit})` : name;
        return { columnName: name, unit, unitType, confidence, displayName };
      }
    }

    // ── Step 5: No match ──
    return {
      columnName: name,
      unit: null,
      unitType: 'unspecified',
      confidence: 'none' as const,
      displayName: name,
    };
  });
}

/**
 * Find the unitType for a given unit string.
 */
export function unitTypeForUnit(unit: string | null): string {
  if (!unit) return 'dimensionless';
  for (const group of UNIT_OPTIONS) {
    if (group.units.some(u => u.value === unit)) return group.unitType;
  }
  return 'unspecified';
}
